import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import superAdminMemberService from '../../services/superAdminMemberService';
import authService from '../../services/authService';

// Material UI components
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Breadcrumbs,
  Card,
  CardContent,
  InputAdornment,
  Avatar,
  Snackbar,
  Skeleton,
  Tabs,
  Tab,
  Divider
} from '@mui/material';

// Material UI icons
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  FilterList as FilterListIcon,
  Person as PersonIcon,
  AccountCircle as DefaultUserIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Schedule as ClockIcon,
  SwapHoriz as SwapHorizIcon,
  Dashboard as DashboardIcon,
  PieChart as PieChartIcon,
  Group as GroupIcon,
  Apartment as ApartmentIcon,
  Timeline as TimelineIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';

// Add chart components
import { 
  AreaChart, Area, BarChart, Bar, PieChart, Pie, 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer, 
  Cell
} from 'recharts';

// Status badge component for member status
const StatusBadge = ({ status }) => {
  const statusConfig = {
    active: {
      color: 'success',
      icon: <CheckCircleIcon fontSize="small" />
    },
    pending: {
      color: 'warning',
      icon: <ClockIcon fontSize="small" />
    },
    inactive: {
      color: 'error',
      icon: <BlockIcon fontSize="small" />
    },
    default: {
      color: 'default',
      icon: <WarningIcon fontSize="small" />
    }
  };

  const config = statusConfig[status] || statusConfig.default;

  return (
    <Chip
      icon={config.icon}
      label={status || 'unknown'}
      color={config.color}
      size="small"
      variant="outlined"
    />
  );
};

const AllUsers = () => {
  const navigate = useNavigate();
  // State for members data
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalMembers, setTotalMembers] = useState(0);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [tenants, setTenants] = useState([]);
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [memberToTransfer, setMemberToTransfer] = useState(null);
  const [newTenantId, setNewTenantId] = useState('');
  
  // Authentication state
  const [authChecked, setAuthChecked] = useState(false);
  
  // CRUD operation states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [memberToEdit, setMemberToEdit] = useState(null);
  const [newMember, setNewMember] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'member',
    status: 'active',
    tenantId: ''
  });
  
  // UI feedback state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Add state for filtered members and sorting
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Tab state for different sections
  const [tabValue, setTabValue] = useState(0);

  // Check authentication on component mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = authService.getCurrentUser();
        if (!user) {
          setError('Please log in to access this page.');
          setAuthChecked(false);
        } else if (user.role !== 'superadmin') {
          setError('You do not have permission to access this page.');
          setAuthChecked(false);
        } else {
          setAuthChecked(true);
          await loadMembers();
          await loadTenants();
          if (tabValue === 1) {
            loadAnalytics();
          }
        }
      } catch (err) {
        console.error("Auth check error:", err);
        setError('Authentication error. Please try logging in again.');
      }
    };
    checkAuth();
  }, []);

  // Effect for filtering and sorting
  useEffect(() => {
    filterMembers();
  }, [members, searchTerm, statusFilter, tenantFilter, sortBy, sortDirection]);

  // Enhanced filter function
  const filterMembers = () => {
    let filtered = [...members];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(member => 
        (member.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (member.lastName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (member.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (member.userId?.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (member.userId?.lastName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (member.userId?.email || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(member => member.status === statusFilter);
    }

    // Apply tenant filter
    if (tenantFilter) {
      filtered = filtered.filter(member => member.tenantId === tenantFilter);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          const aName = `${a.firstName || a.userId?.firstName || ''} ${a.lastName || a.userId?.lastName || ''}`;
          const bName = `${b.firstName || b.userId?.firstName || ''} ${b.lastName || b.userId?.lastName || ''}`;
          comparison = aName.localeCompare(bName);
          break;
        case 'email':
          const aEmail = a.email || a.userId?.email || '';
          const bEmail = b.email || b.userId?.email || '';
          comparison = aEmail.localeCompare(bEmail);
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
          break;
        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '');
          break;
        case 'tenant':
          comparison = (a.tenantName || '').localeCompare(b.tenantName || '');
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    setFilteredMembers(filtered);
  };

  // Load members and tenants on component mount
  useEffect(() => {
    if (authChecked) {
      loadMembers();
      loadTenants();
    }
  }, [authChecked, page, rowsPerPage, statusFilter, tenantFilter]);

  // Load analytics when the analytics tab is selected
  useEffect(() => {
    if (authChecked && tabValue === 1 && !analyticsData) {
      loadAnalytics();
    }
  }, [authChecked, tabValue]);

  // Apply search filter when searchTerm changes
  useEffect(() => {
    if (authChecked) {
      // Add debounce to avoid too many API calls
      const debounceTimer = setTimeout(() => {
        loadMembers();
      }, 500);
      
      return () => clearTimeout(debounceTimer);
    }
  }, [searchTerm, authChecked]);

  // Load members function
  const loadMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading members with filters:', {
        page,
        rowsPerPage,
        searchTerm,
        statusFilter,
        tenantFilter
      });
      
      const filters = {
        status: statusFilter || undefined,
        tenantId: tenantFilter || undefined
      };

      const response = await superAdminMemberService.getAllMembers(
        page + 1,
        rowsPerPage,
        searchTerm,
        filters
      );

      console.log('Members response:', response);

      if (response?.success && Array.isArray(response.data)) {
        setMembers(response.data);
        setTotalMembers(response.pagination?.total || response.data.length);
      } else if (Array.isArray(response)) {
        setMembers(response);
        setTotalMembers(response.length);
      } else if (response?.data && Array.isArray(response.data.data)) {
        setMembers(response.data.data);
        setTotalMembers(response.data.pagination?.total || response.data.data.length);
      } else {
        console.error('Invalid response format:', response);
        setError('Failed to load members: Invalid response format');
        setMembers([]);
      }
    } catch (err) {
      console.error('Error loading members:', err);
      setError(err.message || 'An error occurred while loading members');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  // Load tenants function
  const loadTenants = async () => {
    try {
      console.log('Loading tenants...');
      
      const tenants = await superAdminMemberService.getAllTenants();
      console.log('Tenants loaded:', tenants);
      
      if (Array.isArray(tenants)) {
        setTenants(tenants);
      } else {
        console.warn('Invalid tenants response format:', tenants);
        setTenants([]);
      }
    } catch (err) {
      console.error('Error loading tenants:', err);
      setTenants([]);
    }
  };
  
  // Load analytics data
  const loadAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const data = await superAdminMemberService.getMemberAnalytics();
      console.log('Analytics data:', data);
      setAnalyticsData(data);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setSnackbar({
        open: true,
        message: 'Failed to load analytics data',
        severity: 'error'
      });
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Table pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter handlers
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleTenantFilterChange = (event) => {
    setTenantFilter(event.target.value);
    setPage(0);
  };
  
  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1 && !analyticsData) {
      loadAnalytics();
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setTenantFilter('');
    setPage(0);
  };

  // Sorting handlers
  const handleSortChange = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  // Create member functions
  const openCreateDialog = () => {
    setCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    // Reset new member form
    setNewMember({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      role: 'member',
      status: 'active',
      tenantId: ''
    });
  };

  const handleNewMemberChange = (event) => {
    const { name, value } = event.target;
    setNewMember(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateMember = async () => {
    try {
      setLoading(true);
      
      // Basic validation
      if (!newMember.firstName || !newMember.lastName || !newMember.email || !newMember.password) {
        throw new Error('Please fill in all required fields');
      }
      
      if (!newMember.tenantId) {
        throw new Error('Please select an organization');
      }
      
      // Create the member
      const response = await superAdminMemberService.createMember(newMember);

      // Check response
      if (response?.success && response?.data) {
        // Show success message
        setSnackbar({
          open: true,
          message: 'Member created successfully',
          severity: 'success'
        });
        
        // Refresh the members list
        await loadMembers();
        
        // Close the dialog
        closeCreateDialog();
      } else {
        throw new Error('Failed to create member: Invalid server response');
      }
    } catch (err) {
      console.error('Error creating member:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to create member',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Edit member functions
  const openEditDialog = async (member) => {
    try {
      setLoading(true);
      
      // Get full member details
      const memberDetails = await superAdminMemberService.getMember(member._id);
      console.log('Member details:', memberDetails);
      
      // Format the data
      const formattedMember = {
        _id: member._id,
        firstName: member.firstName || (member.userId && member.userId.firstName) || '',
        lastName: member.lastName || (member.userId && member.userId.lastName) || '',
        email: member.email || (member.userId && member.userId.email) || '',
        status: member.status || 'active',
        tenantId: member.tenantId || '',
        membershipType: member.membershipType || 'regular',
        password: ''
      };
      
      // Set member to edit
      setMemberToEdit(formattedMember);
      
      // Open dialog
      setEditDialogOpen(true);
    } catch (err) {
      console.error('Error fetching member details:', err);
      setSnackbar({
        open: true,
        message: 'Failed to load member details for editing',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setMemberToEdit(null);
  };

  const handleEditMemberChange = (event) => {
    const { name, value } = event.target;
    setMemberToEdit(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateMember = async () => {
    try {
      setLoading(true);
      
      // Basic validation
      if (!memberToEdit.firstName || !memberToEdit.lastName || !memberToEdit.email) {
        throw new Error('Please fill in all required fields');
      }
      
      // Create update payload (omitting password if blank)
      const updatePayload = { ...memberToEdit };
      if (!updatePayload.password) {
        delete updatePayload.password;
      }
      
      // Update the member
      const response = await superAdminMemberService.updateMember(memberToEdit._id, updatePayload);

      // Check response
      if (response?.success && response?.data) {
        // Show success message
        setSnackbar({
          open: true,
          message: 'Member updated successfully',
          severity: 'success'
        });
        
        // Refresh the members list
        await loadMembers();
        
        // Close the dialog
        closeEditDialog();
      } else {
        throw new Error('Failed to update member: Invalid server response');
      }
    } catch (err) {
      console.error('Error updating member:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to update member',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete member functions
  const openDeleteDialog = (member) => {
    setMemberToDelete(member);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setMemberToDelete(null);
  };

  const confirmDelete = async () => {
    if (!memberToDelete) return;

    try {
      setLoading(true);
      
      // Delete the member
      await superAdminMemberService.deleteMember(memberToDelete._id);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Member deleted successfully',
        severity: 'success'
      });
      
      // Refresh the members list
      await loadMembers();
    } catch (err) {
      console.error('Error deleting member:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to delete member',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      closeDeleteDialog();
    }
  };
  
  // Toggle member status
  const toggleMemberStatus = async (member) => {
    try {
      setLoading(true);
      
      // Determine new status
      const newStatus = member.status === 'active' ? 'inactive' : 'active';
      
      // Update the member status
      await superAdminMemberService.updateMember(member._id, { status: newStatus });
      
      // Show success message
      setSnackbar({
        open: true,
        message: `Member ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`,
        severity: 'success'
      });
      
      // Refresh the members list
      await loadMembers();
    } catch (err) {
      console.error('Error updating member status:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to update member status',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Transfer member functions
  const openTransferDialog = (member) => {
    setMemberToTransfer(member);
    setTransferDialogOpen(true);
    setNewTenantId('');
  };

  const closeTransferDialog = () => {
    setTransferDialogOpen(false);
    setMemberToTransfer(null);
    setNewTenantId('');
  };

  const handleTransferMember = async () => {
    if (!memberToTransfer || !newTenantId) {
      setSnackbar({
        open: true,
        message: 'Member and new organization are required',
        severity: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      
      // Transfer the member
      const response = await superAdminMemberService.transferMember(memberToTransfer._id, newTenantId);
      
      // Check response
      if (response?.success) {
        // Show success message
        setSnackbar({
          open: true,
          message: 'Member transferred successfully',
          severity: 'success'
        });
        
        // Refresh the members list
        await loadMembers();
        
        // Close the dialog
        closeTransferDialog();
      } else {
        throw new Error('Failed to transfer member: Invalid server response');
      }
    } catch (err) {
      console.error('Error transferring member:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to transfer member',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Snackbar close handler
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Get tenant name by ID
  const getTenantName = (tenantId) => {
    if (!tenantId) return 'N/A';
    const tenant = tenants.find(t => t._id === tenantId);
    return tenant ? tenant.name : 'Unknown Organization';
  };

  // Render table actions
  const renderTableActions = (member) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Tooltip title="Edit Member">
        <IconButton 
          size="small" 
          color="primary"
          onClick={() => openEditDialog(member)}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      
      <Tooltip title="Delete Member">
        <IconButton 
          size="small" 
          color="error"
          onClick={() => openDeleteDialog(member)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      
      {member.status !== 'active' ? (
        <Tooltip title="Activate Member">
          <IconButton 
            size="small" 
            color="success"
            onClick={() => toggleMemberStatus(member)}
          >
            <CheckCircleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Deactivate Member">
          <IconButton 
            size="small" 
            color="warning"
            onClick={() => toggleMemberStatus(member)}
          >
            <BlockIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Transfer Member">
        <IconButton 
          size="small" 
          color="primary"
          onClick={() => openTransferDialog(member)}
        >
          <SwapHorizIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
  
  // Render the member list tab
  const renderMembersTab = () => (
    <>
      {/* Search and Filters */}
      <Paper 
        elevation={2}
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: 2,
          borderLeft: '4px solid #3f51b5'
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Search members"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel id="tenant-filter-label">Organization</InputLabel>
              <Select
                labelId="tenant-filter-label"
                value={tenantFilter}
                label="Organization"
                onChange={handleTenantFilterChange}
              >
                <MenuItem value="">All Organizations</MenuItem>
                {tenants.map((tenant) => (
                  <MenuItem key={tenant._id} value={tenant._id}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl> 
          </Grid>
          
          <Grid item xs={12} sm={2}>
            <Button 
              fullWidth
              variant="outlined"
              onClick={clearFilters}
              startIcon={<FilterListIcon />}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Members Table */}
      <Paper 
        elevation={2}
        sx={{ 
          borderRadius: 2,
          overflow: 'hidden' 
        }}
      >
        {/* Table header */}
        <Box 
          sx={{ 
            px: 3, 
            py: 2, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6" component="h2">
            Members
          </Typography>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={loadMembers}
          >
            Refresh
          </Button>
        </Box>

        {/* Loading state */}
        {loading ? (
          <Box p={3}>
            {[1, 2, 3, 4, 5].map((item) => (
              <Box key={item} mb={2}>
                <Skeleton variant="rectangular" height={40} />
              </Box>
            ))}
          </Box>
        ) : filteredMembers.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography color="text.secondary" gutterBottom>No members found.</Typography>
            <Button 
              variant="outlined" 
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
            >
              Add Your First Member
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell 
                    sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSortChange('name')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Member Name
                      {sortBy === 'name' && (
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSortChange('email')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Email Address
                      {sortBy === 'email' && (
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSortChange('status')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Status
                      {sortBy === 'status' && (
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                    onClick={() => handleSortChange('tenant')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Organization Name
                      {sortBy === 'tenant' && (
                        <Typography variant="caption" sx={{ ml: 1 }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMembers.map((member) => {
                  // Determine user's name accounting for different data structures
                  const firstName = member.firstName || (member.userId && member.userId.firstName) || '';
                  const lastName = member.lastName || (member.userId && member.userId.lastName) || '';
                  const fullName = `${firstName} ${lastName}`.trim() || 'Unknown User';
                  
                  // Get email accounting for different data structures
                  const email = member.email || (member.userId && member.userId.email) || 'No Email';
                  
                  // Get tenant name with highlighting
                  const tenantName = member.tenantName || getTenantName(member.tenantId) || 'N/A';
                  
                  // Get member initials for the avatar
                  const memberInitials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();

                  return (
                    <TableRow 
                      key={member._id}
                      hover
                      sx={{ 
                        '&:nth-of-type(even)': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } 
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ 
                            mr: 2, 
                            bgcolor: 'primary.main',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)' 
                          }}>
                            {memberInitials || <DefaultUserIcon />}
                          </Avatar>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {fullName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontFamily: 'monospace', 
                            backgroundColor: 'rgba(0, 0, 0, 0.04)', 
                            p: 0.5, 
                            borderRadius: 1,
                            display: 'inline-block'
                          }}
                        >
                          {email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={member.status} />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={tenantName} 
                          size="small" 
                          variant="outlined"
                          color="primary"
                          sx={{ fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell>
                        {renderTableActions(member)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={totalMembers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </>
  );

  // Render analytics tab
  const renderAnalyticsTab = () => (
    <>
      {analyticsLoading ? (
        <Box p={3} textAlign="center">
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading analytics data...
          </Typography>
        </Box>
      ) : !analyticsData ? (
        <Box p={3} textAlign="center">
          <Alert severity="info">
            No analytics data available. Try refreshing.
          </Alert>
          <Button 
            variant="outlined" 
            onClick={loadAnalytics} 
            startIcon={<RefreshIcon />}
            sx={{ mt: 2 }}
          >
            Refresh Analytics
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Summary statistics */}
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                <DashboardIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Member Summary
              </Typography>
              <Box sx={{ my: 2 }}>
                <Typography variant="h3" color="primary" fontWeight="bold">
                  {analyticsData.totalMembers}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Members
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1">
                  <CheckCircleIcon color="success" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {analyticsData.byStatus.active} Active Members
                </Typography>
                <Typography variant="body1">
                  <ClockIcon color="warning" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {analyticsData.byStatus.pending} Pending Members
                </Typography>
                <Typography variant="body1">
                  <BlockIcon color="error" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {analyticsData.byStatus.inactive} Inactive Members
                </Typography>
              </Box>
            </Paper>
          </Grid>
          
          {/* Growth chart */}
          <Grid item xs={12} md={9}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Member Growth Over Time
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={analyticsData.growth} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <defs>
                    <linearGradient id="colorMembers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3f51b5" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#3f51b5" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <RechartsTooltip />
                  <Area 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#3f51b5" 
                    fillOpacity={1} 
                    fill="url(#colorMembers)" 
                    name="New Members" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          
          {/* Members by organization */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                <ApartmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Members by Organization
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analyticsData.byTenant} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis 
                    type="category" 
                    dataKey="tenantName" 
                    tick={{ fontSize: 12 }}
                    width={100}
                  />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#8884d8" name="Members">
                    {analyticsData.byTenant.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={`#${Math.floor(Math.random()*16777215).toString(16)}`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          
          {/* Members by type */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                <PieChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Members by Type
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie 
                    data={analyticsData.byType} 
                    cx="50%" 
                    cy="50%" 
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80} 
                    fill="#8884d8" 
                    dataKey="count"
                    nameKey="membershipType"
                  >
                    {analyticsData.byType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={`#${Math.floor(Math.random()*16777215).toString(16)}`} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
    </>
  );

  return (
    <Box sx={{ p: 3, mt: { xs: 8, md: 2 } }}>
      <Container maxWidth="xl">
        {/* Page Header */}
        <Box my={4}>
          <Breadcrumbs sx={{ mb: 2 }}>
            <Link 
              to="/superadmin/dashboard"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              Dashboard
            </Link>
            <Typography color="text.primary">Members</Typography>
          </Breadcrumbs>
          
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
                Member Management
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage all members across organizations
              </Typography>
            </Grid>
            <Grid item>
              <Button 
                variant="contained"
                onClick={openCreateDialog}
                startIcon={<AddIcon />}
                sx={{ 
                  mr: 1,
                  background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
                  boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
                }}
              >
                Create Member
              </Button>
              <Button 
                variant="outlined"
                onClick={loadMembers}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Quick Stats */}
        <Grid container spacing={3} mb={4}>
          {/* Total Members */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper 
              elevation={2}
              sx={{ 
                p: 2, 
                display: 'flex', 
                alignItems: 'center', 
                borderLeft: '4px solid #3f51b5',
                height: '100%' 
              }}
            >
              <Avatar sx={{ bgcolor: 'rgba(63, 81, 181, 0.1)', color: '#3f51b5' }}>
                <PersonIcon />
              </Avatar>
              <Box ml={2}>
                <Typography variant="body2" color="text.secondary">Total Members</Typography>
                <Typography variant="h5">{totalMembers}</Typography>
              </Box> 
            </Paper>
          </Grid>
          
          {/* Active Members */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderTop: '4px solid #4caf50' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Active Members
                </Typography>
                <Typography variant="h3">
                  {loading ? <CircularProgress size={30} /> : 
                    members.filter(m => m.status === 'active').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Inactive Members */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderTop: '4px solid #f44336' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Inactive Members
                </Typography>
                <Typography variant="h3">
                  {loading ? <CircularProgress size={30} /> : 
                    members.filter(m => m.status === 'inactive').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Pending Members */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderTop: '4px solid #ff9800' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Pending Members
                </Typography>
                <Typography variant="h3">
                  {loading ? <CircularProgress size={30} /> : 
                    members.filter(m => m.status === 'pending').length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs for different views */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            aria-label="member management tabs"
          >
            <Tab 
              label="Members" 
              icon={<GroupIcon />} 
              iconPosition="start" 
            />
            <Tab 
              label="Analytics" 
              icon={<PieChartIcon />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Error message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Tab content */}
        {tabValue === 0 ? renderMembersTab() : renderAnalyticsTab()}

        {/* Create Member Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={closeCreateDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Create New Member</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={newMember.firstName}
                  onChange={handleNewMemberChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={newMember.lastName}
                  onChange={handleNewMemberChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={newMember.email}
                  onChange={handleNewMemberChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={newMember.password}
                  onChange={handleNewMemberChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={newMember.status}
                    label="Status"
                    onChange={handleNewMemberChange}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Organization</InputLabel>
                  <Select
                    name="tenantId"
                    value={newMember.tenantId}
                    label="Organization *"
                    onChange={handleNewMemberChange}
                  >
                    {tenants.map((tenant) => (
                      <MenuItem key={tenant._id} value={tenant._id}>
                        {tenant.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCreateDialog} startIcon={<CancelIcon />}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateMember} 
              color="primary" 
              variant="contained" 
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Member Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={closeEditDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Member</DialogTitle>
          <DialogContent>
            {memberToEdit && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={memberToEdit.firstName || ''}
                    onChange={handleEditMemberChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={memberToEdit.lastName || ''}
                    onChange={handleEditMemberChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={memberToEdit.email || ''}
                    onChange={handleEditMemberChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="New Password (leave blank to keep unchanged)"
                    name="password"
                    type="password"
                    value={memberToEdit.password || ''}
                    onChange={handleEditMemberChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      name="status"
                      value={memberToEdit.status || 'active'}
                      label="Status"
                      onChange={handleEditMemberChange}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Organization</InputLabel>
                    <Select
                      name="tenantId"
                      value={memberToEdit.tenantId || ''}
                      label="Organization"
                      onChange={handleEditMemberChange}
                    >
                      {tenants.map((tenant) => (
                        <MenuItem key={tenant._id} value={tenant._id}>
                          {tenant.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeEditDialog} startIcon={<CancelIcon />}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateMember} 
              color="primary" 
              variant="contained" 
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={closeDeleteDialog}
        >
          <DialogTitle>Confirm Member Deletion</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the member "{
                (memberToDelete?.firstName || memberToDelete?.userId?.firstName || '') + ' ' + 
                (memberToDelete?.lastName || memberToDelete?.userId?.lastName || '')
              }" ({memberToDelete?.email || memberToDelete?.userId?.email})?
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteDialog} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={confirmDelete} 
              color="error" 
              variant="contained" 
              startIcon={<DeleteIcon />}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Transfer Dialog */}
        <Dialog
          open={transferDialogOpen}
          onClose={closeTransferDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Transfer Member to Different Organization
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Select the organization you want to transfer this member to.
            </DialogContentText>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Organization</InputLabel>
              <Select
                value={newTenantId}
                label="Organization"
                onChange={(e) => setNewTenantId(e.target.value)}
              >
                {tenants
                  .filter(tenant => tenant._id !== memberToTransfer?.tenantId)
                  .map((tenant) => (
                    <MenuItem key={tenant._id} value={tenant._id}>
                      {tenant.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeTransferDialog} startIcon={<CancelIcon />}>
              Cancel
            </Button>
            <Button
              onClick={handleTransferMember}
              color="primary"
              variant="contained"
              startIcon={<SwapHorizIcon />}
              disabled={!newTenantId || loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Transfer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity} 
            sx={{ width: '100%' }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default AllUsers;
